import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../common/Icon';

export interface DetailHeaderProps {
  /** The page title — plain text or a ReactNode for custom layout. */
  title: ReactNode;
  /** Optional one-line subtitle below the title (typically a count
   * like "3 members · 2 agents" or a timestamp). */
  subtitle?: ReactNode;
  /** Right-aligned action area — typically one `<IconButton>`. */
  actions?: ReactNode;
  /** Override the back navigation. Defaults to `navigate(-1)`. */
  onBack?: () => void;
}

/**
 * Header for detail pages — anything reached by drilling in from a
 * list. Has a back arrow on the left, centered title (with optional
 * subtitle), optional right action.
 *
 * For tab-root pages use `<PageHeader>` instead — that one has no
 * back arrow and a left-aligned title.
 *
 * @example
 * <DetailHeader
 *   title={agent.name}
 *   subtitle={`${agent.memberCount} members`}
 *   actions={<IconButton icon="more_horiz" onClick={openMenu} />}
 * />
 */
export function DetailHeader({
  title,
  subtitle,
  actions,
  onBack,
}: DetailHeaderProps) {
  const navigate = useNavigate();
  const handleBack = () => (onBack ? onBack() : navigate(-1));

  return (
    <div className="border-b border-gray-100 dark:border-white/[0.06]">
      <div className="flex items-center gap-2 px-2 h-12">
        <button
          type="button"
          onClick={handleBack}
          className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-colors"
          aria-label="Back"
        >
          <Icon name="arrow_back_ios" size={18} className="text-gray-700 dark:text-gray-300" />
        </button>
        <div className="flex-1 min-w-0 text-center">
          {typeof title === 'string' ? (
            <div className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate">
              {title}
            </div>
          ) : (
            title
          )}
          {subtitle && (
            <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate leading-tight">
              {subtitle}
            </div>
          )}
        </div>
        <div className="flex-shrink-0 w-9 h-9 flex items-center justify-end">
          {actions}
        </div>
      </div>
    </div>
  );
}
